import { Fragment } from "react";
import { Chart, ChartTypes, formatNumber } from "@uicore";
import { Bar, TooltipProps } from "recharts";
import dayjs from "dayjs";
import { Stack, Typography } from "@altimateai/lego";
import CostChartTooltip from "../../../summary/components/CostChartTooltip";
import { formatProductName } from "../../helpers";
import { useAppState } from "@/modules/app/useAppContext";
import {
  databricksColorFor,
  ESTIMATE_OPACITY,
  getGroupColor,
  getProductColor,
  SUMMARY_TIME_SERIES_CHART_PROPS,
  type TrendsGroup,
} from "./costConstants";

interface Props {
  data: Record<string, unknown>[];
  segments: string[];
  selectedGroup: TrendsGroup | null;
}

const DatabricksFutureChart = ({ data, segments, selectedGroup }: Props) => {
  const { currency } = useAppState();

  // Category tabs are always shown (static list) — when the selected category
  // has no usage the BE returns an empty graph/segments list. Surface an
  // explicit empty state rather than rendering a blank chart canvas.
  if (segments.length === 0 || data.length === 0) {
    return (
      <Stack align="center" justify="center" className="al-h-full al-w-full">
        <Typography variant="body" className="al-text-muted-foreground">
          {selectedGroup
            ? `No projected ${selectedGroup} costs for this date range.`
            : "No projected costs for this date range."}
        </Typography>
      </Stack>
    );
  }

  // In total mode segments are spec-group labels; in category mode they are
  // raw product/tier names that need display formatting.
  const segmentLabel = (segment: string) => (selectedGroup ? formatProductName(segment) : segment);
  const segmentColor = (segment: string) =>
    selectedGroup ? getProductColor(segment, selectedGroup) : getGroupColor(segment);

  return (
    <Chart
      {...SUMMARY_TIME_SERIES_CHART_PROPS}
      title={selectedGroup ? `${selectedGroup} Cost Projections` : "Cost Projections by Category"}
      xAxisDataKey="period"
      yAxisLabel={{ value: "Cost" }}
      xAxisLabelFormatter={(value: string) => dayjs(value).format("MMM YYYY")}
      yAxisLabelFormatter={(value: string) =>
        formatNumber(Number(value), { currency, notation: "compact" })
      }
      type={ChartTypes.BarChart}
      data={data}
      tooltipProps={{
        content: (props: TooltipProps<number, string>) => {
          const fields = [
            {
              key: { current: "total", estimate: "total_estimate" },
              label: "Total",
              isTotal: true,
              hideSticker: true,
              showChangePercentage: true,
            },
            ...segments.map(segment => ({
              key: { current: segment, estimate: `${segment}_estimate` },
              label: segmentLabel(segment),
              showChangePercentage: true,
            })),
          ];
          return (
            <CostChartTooltip
              {...props}
              timeRange="month"
              dateKey="period"
              isFuture
              fields={fields}
              colorFor={databricksColorFor}
            />
          );
        },
      }}
    >
      {({ barSize, selectedLegend }) => (
        <>
          {segments.map((segment, index) => {
            const color = segmentColor(segment);
            const isLast = index === segments.length - 1;
            // Legend click passes the bar's dataKey; match both actual and
            // projected dataKeys so clicking either reveals both bars.
            const matchesLegend =
              !selectedLegend ||
              selectedLegend === segment ||
              selectedLegend === `${segment}_estimate`;
            return (
              <Fragment key={segment}>
                <Bar
                  barSize={barSize}
                  name={segmentLabel(segment)}
                  dataKey={segment}
                  stackId="cost"
                  fill={color}
                  hide={!matchesLegend}
                />
                <Bar
                  barSize={barSize}
                  name={`${segmentLabel(segment)} (projected)`}
                  dataKey={`${segment}_estimate`}
                  stackId="cost"
                  fill={color}
                  fillOpacity={ESTIMATE_OPACITY}
                  hide={!matchesLegend}
                  radius={isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                />
              </Fragment>
            );
          })}
        </>
      )}
    </Chart>
  );
};

export default DatabricksFutureChart;
